import { values } from 'lodash';
import { DyProductSource } from 'src/interfaces/Component';
import { ComponentTypes } from 'src/interfaces/ComponentTypesList';
import { type IAnimation, AnimationTypes } from 'src/interfaces/Product';
import { ProjectConfigInterface } from 'src/interfaces/ProjectConfig';
import { lang } from 'src/utils/language';
import { colorPickerColors } from 'src/utils/theme';

export const ZOOM_BAR_HEIGHT = 50;
export const SIDE_NAV_WIDTH = 84;
export const SIDEBAR_WIDTH = 200;
export const SUB_NAV_WIDTH = 62;
export const LIST_ASSEMBLY_PAGE_MAX_RECORDS = 200;
export const LIST_ASSEMBLY_PAGE_MAX_FILTER_CHARACTERS = 7000;
export const GRID_CONTAINER_BORDER_WIDTH = 60;
export const SECTION_BORDER_WIDTH = 15;
export const SCHEDULE_COMPONENT_SCREEN_BORDER_WIDTH = 24;
export const LAYOUT_CARD_WIDTH = 248;
export const FIXED_FOOTER_HEIGHT = 60;
export const STEPPER_HEADER_HEIGHT = 203;
export const STEPPER_HEADER_HEIGHT_WITHOUT_TITLE = 70;
export const SECTION_NAME_FONT = 4.5;
export const FONTS_OPTIONS = ['Speedee_W_Rg', 'Speedee_W_Bd'];
export const SIDEBAR_WITHOUT_SUBBAR = 400;
export const ISO_DATE_FORMAT = 'YYYY-MM-DD';
export const US_DATE_FORMAT = 'MM-DD-YYYY';
export const US_DATE_SLASH_FORMAT = 'MM/DD/YYYY';
export const US_TIMESTAMP_FORMAT = 'MM-DD-YYYY hh:mm A';
export const ADVANCED_DATE_FORMAT = 'Do MMMM YYYY';
export const COMPONENT_LIST_CONTAINER_HEIGHT =
  STEPPER_HEADER_HEIGHT + FIXED_FOOTER_HEIGHT + 36;
export const ASSEMBLY_TABLE_CONTAINER_HEIGHT =
  STEPPER_HEADER_HEIGHT + FIXED_FOOTER_HEIGHT + 20;
export const SECTION_CONTAINER_HEIGHT =
  STEPPER_HEADER_HEIGHT + FIXED_FOOTER_HEIGHT + 130;
export const SECTION_SELECTOR_CONTAINER_HEIGHT =
  STEPPER_HEADER_HEIGHT + FIXED_FOOTER_HEIGHT + 20;
export const INITIAL_SEARCH_AREA_HEIGHT = 30;
export const INITIAL_SEARCH_AREA_BORDER_RADIUS = 90;
export const MAX_SEARCH_AREA_HEIGHT = 180;
export const MIN_SEARCH_AREA_BORDER_RADIUS = 30;
export const MAX_DESCRIPTION_LENGTH = 250;
export const MAX_LOCATION_TAG = 50;
export const LOCATION_PAGE_CONTENT_VERTICAL_PADDING = 12;
export const LOCATION_PAGE_CONTENT_PADDING_LEFT = 30;
export const LOCATION_PAGE_CONTENT_PADDING_RIGHT = 24;
export const LOCATION_TAG_PAGE_SIZE = 20;
export const DEFAULT_DURATION = 15000; // Default duration in milliseconds for positions of Component Scheduling
export const DEFAULT_POSITION = 4; // Default position number for positions of Component Scheduling
export const DEFAULT_POSITION_ALL = 'all';
export const DEFAULT_SCREEN_ALL = 'all';
export const MAX_ASSEMBLY_ROTATION_DURATION_SECONDS = 600;
export const MIN_TABLE_HEIGHT = '50vh';
export const MAX_ROWS_PER_PAGE = 50;
export const LISTVIEW_MAX_ROWS_PER_PAGE = 20;
export const OO_LOCATIONS_PAGE_SIZE = 20; // Default page size for locations in the Outage Override page
export const CALENDAR_HEADER_HEIGHT = 40;
export const LOCATION_HEADER_HEIGHT = 75;
export const LOCATION_GROUP_CALENDAR_VIEW_HEADER_TAB_HEIGHT = 108;
export const LOCATION_GROUP_SCHEDULE_VIEW_HEADER_TAB_HEIGHT = 84;
export const LOCATION_GROUP_SCHEDULE_VIEW_TABLE_HEADER_HEIGHT = 104;

export const LOCATION_CALENDAR_VIEW_HEADER_TAB_HEIGHT = 64;
export const LOCATION_HEADER_LOCATION_GROUP_TAB_HEIGHT = 49;
export const LOCATION_HEADER_LOCATION_GROUP_TAB_INFO_HEIGHT = 165;
export const TOP_NAV_HEIGHT = 80;

export const LOCATION_HEADER_DAYPART_OFF_ALERT_HEIGHT = 48;

const DEFAULT_ANIMATION_BASE_DATA = {
  in: {
    delay: 0,
    type: AnimationTypes.FADE,
  },
  out: null,
};

export const EMPTY_ANIMATION_DATA: IAnimation = {
  ...DEFAULT_ANIMATION_BASE_DATA,
  productName: { ...DEFAULT_ANIMATION_BASE_DATA },
  price: { ...DEFAULT_ANIMATION_BASE_DATA },
  label: { ...DEFAULT_ANIMATION_BASE_DATA },
  nutrition: { ...DEFAULT_ANIMATION_BASE_DATA },
  disclaimer: { ...DEFAULT_ANIMATION_BASE_DATA },
};

type ComponentDisplayNameMap = {
  [key in ComponentTypes]: string;
};

export const COMPONENT_DISPLAY_LITERALS_MAP: Partial<ComponentDisplayNameMap> =
  {
    [ComponentTypes.SINGLE_SHELF]: lang.componentTypes.singleShelf,
    [ComponentTypes.PROMOTIONAL_SPACE]: lang.componentTypes.promotionalSpace,
    [ComponentTypes.LISTING_WITH_OPTIONAL_VISUALS]:
      lang.componentTypes.listingWithOptionalVisuals,
    [ComponentTypes.LISTING_VISUALS_GALLERY]:
      lang.componentTypes.listingWithVisualGallery,
    [ComponentTypes.COMBO_STYLE]: lang.componentTypes.comboStyle,
    [ComponentTypes.COD]: lang.componentTypes.cod,
    [ComponentTypes.COD_THANK_YOU]: lang.componentTypes.codThankYou,
    [ComponentTypes.DISCLAIMER]: lang.componentTypes.disclaimer,
    [ComponentTypes.PROMOTIONAL_COMPONENT]:
      lang.componentTypes.promotionalComponent,
  };

export const enabledComponentNames: ComponentTypes[] = [
  ComponentTypes.SINGLE_SHELF,
  ComponentTypes.COMBO_STYLE,
  ComponentTypes.LISTING_WITH_OPTIONAL_VISUALS,
  ComponentTypes.LISTING_VISUALS_GALLERY,
  ComponentTypes.PROMOTIONAL_SPACE,
  ComponentTypes.DISCLAIMER,
  ComponentTypes.COD,
  ComponentTypes.COD_THANK_YOU,
];

export const defaultBrandedColors = values(colorPickerColors);

export const OLD_ALA_CARTE_KEY = 'alacarte';
export const NEW_ALA_CARTE_KEY = 'à la carte';

export const FONT_FAMILY_OPTIONS = [
  { label: 'Speedee_W_Bd', value: 'Speedee_W_Bd' },
  { label: 'Speedee_W_Rg', value: 'Speedee_W_Rg' },
];

export const FLEX_WRAP_OPTIONS = [
  { label: 'No wrap', value: 'nowrap' },
  { label: 'Wrap', value: 'wrap' },
];

export const DEFAULT_TIME = '00:00:00';
export const DyHeader = {
  suggestion: {
    knownCustomer: 'Add another fave',
    unknownCustomer: 'Want a little more?',
  },
  recommendation: {
    knownCustomer: 'Picked for you',
    unknownCustomer: 'Fan Favorites',
  },
};
export const SUPPORTED_FILE_TYPES =
  'image/png, image/jpeg, image/gif, image/jpg, video/mp4, video/webm';

export const ASSET_UPLOAD_SUPPORTED_FILE_TYPES =
  'image/png, image/jpeg, image/gif, image/jpg, video/webm';
export const ASSET_UPLOAD_SUPPORTED_FILE_TYPES_OUTAGE = 'image/png';
export const ASSET_UPLOAD_SUPPORTED_FILE_TYPES_PRODUCT =
  'image/png, image/jpg, image/jpeg, image/gif';

export const SUPPORTED_EXTENSIONS = [
  'png',
  'jpg',
  'jpeg',
  'gif',
  'mp4',
  'webm',
];
export const ASSEMBLY_PUBLICATION_HEADERS = [
  'Bulk replace name',
  'Bulk replace ID',
  'Assembly name',
  'Assembly ID',
  'Assembly link',
  'From component name',
  'From component ID',
  'To component name',
  'To component ID',
  'User ID',
  'User role',
  'Last modified',
];

export const ASSEMBLY_EXPORT_HEADERS = [
  { label: 'Name', key: 'name' },
  { label: 'Channel', key: 'channel' },
  { label: 'Type', key: 'type' },
  { label: 'Staged', key: 'staged' },
  { label: 'Status', key: 'status' },
];

export const BULK_REPLACE_EXPORT_HEADERS = [
  { label: 'Location Group Name', key: 'locationGroupName' },
  { label: 'Schedule Name', key: 'scheduleName' },
  { label: 'Schedule ID', key: 'scheduleId' },
  { label: 'From component name', key: 'originalComponent' },
  { label: 'From component ID', key: 'fromComponentId' },
  { label: 'To component name', key: 'replacementComponent' },
  { label: 'To component ID', key: 'toComponentId' },
  { label: 'Daypart', key: 'daypart' },
  { label: 'Start Date', key: 'startDate' },
  { label: 'End Date', key: 'endDate' },
  { label: 'User ID', key: 'userId' },
  { label: 'User Role', key: 'userRole' },
  { label: 'Last modified', key: 'lastModified' },
];

export const ALLOWED_FILE_EXTENSIONS = ['png', 'jpg', 'jpeg', 'gif', 'webm'];
export const ALLOWED_FILE_EXTENSIONS_OUTAGE = ['png'];
export const ALLOWED_FILE_EXTENSIONS_PRODUCT = ['png', 'jpg', 'jpeg', 'gif'];

export enum QueryOperator {
  OR = '|',
  AND = '&',
}

export const CURRENCY_POSITION_OPTIONS = [
  {
    label: lang.left,
    value: 'before',
  },
  {
    label: lang.right,
    value: 'after',
  },
];

export const DEFAULT_ASSEMBLY_COMPONENT_CONFIG = {
  transition: 'fade',
  duration: 3000,
};

export const BACKUP_ASSEMBLY_COMPONENT_CONFIG = {
  transition: 'fade',
  duration: 15000,
};

export const MENU_ITEM_WIDTH = 236;
export const INPUT_KEY_DEBOUNCE_MS = 500;

export const COLLECTIONS = {
  ASSEMBLIES: 'assemblies',
  ASSEMBLIES_COMPONENTS: 'assemblyComponents',
  COMPONENTS: 'components',
  LOCATION_GROUPS: 'locationGroups',
  LOCATIONS: 'locations',
  LOCATION_TAGS: 'locationTags',
  PRODUCT_CATEGORIES: 'productCategories',
  SCHEDULE_OVERRIDES: 'scheduleOverrides',
  SCHEDULE_DAYPARTS: 'scheduleDayparts',
  SCHEDULE_ASSEMBLIES: 'scheduleAssemblies',
  DEFAULT_ASSEMBLIES: 'defaultAssemblies',
  SCHEDULE_DAYPART_OVERRIDES: 'scheduleDaypartOverrides',
  PRODUCTS: 'products',
  PROJECT_CONFIGS: 'projectConfigs',
  THEMES: 'themes',
  ASSETS: 'assets',
  SCREEN_SECTIONS: 'screenSections',
  LAYOUT_SCREENS: 'layoutScreens',
  PRODUCT_VARIATIONS: 'productVariations',
  VARIATION_TEMPLATES: 'variationTemplates',
  SCREENS: 'screens',
  SCHEDULE_PAYPART_OVERRIDES: 'schedulePaypartOverrides',
  SCHEDULE_COMPONENTS: 'scheduleComponents',
  SCHEDULE_COMPONENTS_WITH_LGS: 'scheduleComponentsWithMultiLocationGroups',
  SCHEDULE_ITEMS: 'scheduleItems',
  SCHEDULE: 'schedule',
  SCHEDULES: 'schedules',
  SCHEDULE_ITEM_OVERRIDES: 'scheduleItemOverrides',
  BULK_REPLACEMENT: 'bulkReplacement',
  SCHEDULE_ITEM_BACKUP: 'scheduleItemBackup',
  DAYPART_GLOBAL_COMPONENTS: 'daypartGlobalComponents',
  GLOBAL_COMPONENTS_SCREENS: 'globalComponentsScreens',
};

export const USER_ACTIONS = {
  CREATE: 'CREATE',
  UPDATE: 'UPDATE',
  DELETE: 'DELETE',
};

export enum CodPositions {
  LEFT = 'left',
  RIGHT = 'right',
}

export enum INoScreens {
  ONE = '1',
  TWO = '2',
  THREE = '3',
  FOUR = '4',
  FIVE = '5',
  SIX = '6',
  SEVEN = '7',
}

export enum IOrientation {
  VERTICAL = 'vertical',
  HORIZONTAL = 'horizontal',
  MIXED = 'mixed',
}
export enum IOrientationLabel {
  vertical = 'Vertical',
  horizontal = 'Horizontal',
  mixed = 'Mixed',
}

export enum ScreenNames {
  PRESELL = 'Presell',
  MENU1 = 'Menu 1',
  MENU2 = 'Menu 2',
  MENU = 'Menu',
}

export enum ScreenLayoutTypeEnum {
  MENU = 'Menu',
  PRESELL_MENU = 'Presell + Menu',
  TWO_MENUS = 'Menu 1 + Menu 2',
  PRESELL_TWO_MENUS = 'Presell + Menu 1 + Menu 2',
}

export const DRIVE_THRU_3_SCREEN_NAMES: Record<
  CodPositions,
  Record<number, string>
> = {
  [CodPositions.LEFT]: {
    1: ScreenNames.PRESELL,
    2: ScreenNames.MENU1,
    3: ScreenNames.MENU2,
  },
  [CodPositions.RIGHT]: {
    1: ScreenNames.MENU2,
    2: ScreenNames.MENU1,
    3: ScreenNames.PRESELL,
  },
};

export type ScreenLayoutTypeFor2Screens =
  | ScreenLayoutTypeEnum.PRESELL_MENU
  | ScreenLayoutTypeEnum.TWO_MENUS;

export const DRIVE_THRU_2_SCREEN_NAMES: Record<
  CodPositions,
  Record<ScreenLayoutTypeFor2Screens, Record<number, string>>
> = {
  [CodPositions.LEFT]: {
    [ScreenLayoutTypeEnum.PRESELL_MENU]: {
      1: ScreenNames.PRESELL,
      2: ScreenNames.MENU,
    },
    [ScreenLayoutTypeEnum.TWO_MENUS]: {
      1: ScreenNames.MENU1,
      2: ScreenNames.MENU2,
    },
  },
  [CodPositions.RIGHT]: {
    [ScreenLayoutTypeEnum.PRESELL_MENU]: {
      1: ScreenNames.MENU,
      2: ScreenNames.PRESELL,
    },
    [ScreenLayoutTypeEnum.TWO_MENUS]: {
      1: ScreenNames.MENU2,
      2: ScreenNames.MENU1,
    },
  },
};

export const DRIVE_THRU_1_SCREEN_NAME = { 1: ScreenNames.MENU };

export type CodPosition = CodPositions | null;

export const SCREEN_CONFIGURATION_LABELS = {
  TWO_VERTICAL_SCREENS: '2 vertical screens',
  TWO_HORIZONTAL_SCREENS: '2 horizontal screens',
} as const;

export const layoutTypeToPositionsKey: Record<
  string,
  keyof Pick<
    ProjectConfigInterface,
    | 'idmbPositionNumber'
    | 'odmbPositionNumber'
    | 'mcCafePositionNumber'
    | 'merchandiserPositionNumber'
  >
> = {
  Indoor: 'idmbPositionNumber',
  Outdoor: 'odmbPositionNumber',
  McCafe: 'mcCafePositionNumber',
  'Digital Merchandiser': 'merchandiserPositionNumber',
};

export const MAX_ALLOWED_COMPONENTS = 5;
export const LISTVIEW_DROPDOWN_OPTIONS = [
  {
    label: lang.scheduledComponents,
    value: 'schedule_component',
  },
  {
    label: lang.defaultAssembly,
    value: 'default_assembly',
  },
];
export const NAVIGATION_STATE = {
  FROM_SCHEDULE_COMPONENT_LISTVIEW: 'fromScheduleComponentListview',
};
export const getNoDaypartMessage = (shouldShowListview?: {
  daypartDisabled?: boolean;
  noDefaultAssembly?: boolean;
  noScheduleComponent?: boolean;
}): string | null => {
  if (shouldShowListview?.daypartDisabled) return lang.noDaypartsEnabled;
  if (shouldShowListview?.noDefaultAssembly) return lang.noDefaultAssembly;
  if (shouldShowListview?.noScheduleComponent)
    return lang.noScheduledComponents;

  return null;
};

export const excludeComponentsForBulkReplacement = ['cod', 'cod-thank-you'];

export const DisallowedComponentTypes = [
  DyProductSource.DYS,
  DyProductSource.DYR,
  ComponentTypes.COD,
  ComponentTypes.COD_THANK_YOU,
];

export enum ICustomerTriggeredEvents {
  CUSTOMER_ORDER_DISPLAY = 'Customer order display (COD)',
  DYNAMIC_AREA = 'Dynamic area',
  THANK_YOU_SCREEN = 'Thank you screen',
  COD_TRIGGERED_AREA = 'COD triggered area',
}

export const DyProductSourceDimensions = {
  DYS: {
    width: 720,
    height: 440,
  },
  DYR: {
    width: 1080,
    height: 440,
  },
};

export const EVENT_TYPES = {
  SECTION_CLICK: 'section:click',
  PRODUCT_CLICK: 'product:click',
};

export const DYRecommendationsStorage = 'dy-recommendations-storage';
